package com.mgacreative.hotel.feature.admin.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotel.domain.model.Supplier
import com.mgacreative.hotel.domain.model.SupplierCategory
import com.mgacreative.hotel.shared.ui.designsystem.components.verticalScrollbar
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun AdminSupplierManagementScreen(onNavigateBack: () -> Unit) {
    val viewModel = koinViewModel<AdminSupplierViewModel>()
    val categories by viewModel.categories.collectAsState()
    val suppliers by viewModel.suppliers.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()

    var searchQuery by remember { mutableStateOf("") }
    var selectedCategoryId by remember { mutableStateOf<String?>("ALL") }

    // Dialog state
    var showAddCategoryDialog by remember { mutableStateOf(false) }
    var categoryToEdit by remember { mutableStateOf<SupplierCategory?>(null) }
    var showAddSupplierDialog by remember { mutableStateOf(false) }
    var supplierToEdit by remember { mutableStateOf<Supplier?>(null) }

    // Filtreleme
    val filteredSuppliers = remember(suppliers, categories, searchQuery, selectedCategoryId) {
        suppliers.filter { supplier ->
            val matchesCategory = (selectedCategoryId == "ALL") || (supplier.category_id == selectedCategoryId)
            val matchesSearch = searchQuery.isBlank() ||
                    supplier.name.contains(searchQuery, ignoreCase = true) ||
                    (supplier.contact_info?.contains(searchQuery, ignoreCase = true) == true)
            matchesCategory && matchesSearch
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Column {
                        Text(
                            text = "Tedarikçi Yönetimi",
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFF0F172A)
                        )
                        Text(
                            text = "${suppliers.size} Tedarikçi Kayıtlı (${filteredSuppliers.size} listeleniyor)",
                            style = MaterialTheme.typography.bodySmall,
                            color = Color(0xFF64748B)
                        )
                    }
                },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Geri", tint = Color(0xFF0F172A))
                    }
                },
                actions = {
                    Button(
                        onClick = { showAddSupplierDialog = true },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.padding(end = 12.dp)
                    ) {
                        Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(16.dp), tint = Color.White)
                        Spacer(modifier = Modifier.width(6.dp))
                        Text("+ Yeni Tedarikçi", fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = Color.White)
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = Color.White)
            )
        },
        containerColor = Color(0xFFF8FAFC)
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .padding(horizontal = 16.dp)
        ) {
            Spacer(modifier = Modifier.height(12.dp))

            // 1. Arama Çubuğu
            OutlinedTextField(
                value = searchQuery,
                onValueChange = { searchQuery = it },
                placeholder = { Text("Tedarikçi adı veya iletişim bilgisi ara...", fontSize = 14.sp) },
                leadingIcon = { Icon(Icons.Default.Search, contentDescription = "Ara", tint = Color(0xFF64748B)) },
                trailingIcon = {
                    if (searchQuery.isNotEmpty()) {
                        IconButton(onClick = { searchQuery = "" }) {
                            Icon(Icons.Default.Clear, contentDescription = "Temizle", tint = Color(0xFF64748B))
                        }
                    }
                },
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedContainerColor = Color.White,
                    unfocusedContainerColor = Color.White,
                    focusedBorderColor = Color(0xFF2563EB),
                    unfocusedBorderColor = Color(0xFFCBD5E1)
                ),
                singleLine = true
            )

            Spacer(modifier = Modifier.height(12.dp))

            // 2. Kategori Çipleri (Üstte Kompakt Yatay Çubuk)
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Kategori Ekle Butonu
                Surface(
                    shape = RoundedCornerShape(8.dp),
                    color = Color(0xFF0F172A),
                    modifier = Modifier.clickable { showAddCategoryDialog = true }
                ) {
                    Row(
                        modifier = Modifier.padding(horizontal = 10.dp, vertical = 7.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(Icons.Default.Add, contentDescription = null, tint = Color.White, modifier = Modifier.size(14.dp))
                        Spacer(modifier = Modifier.width(4.dp))
                        Text("+ Kategori", fontSize = 12.sp, color = Color.White, fontWeight = FontWeight.SemiBold)
                    }
                }

                Spacer(modifier = Modifier.width(8.dp))

                LazyRow(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    // "Tümü" Çipi
                    item {
                        val isAllSelected = selectedCategoryId == "ALL"
                        Surface(
                            shape = RoundedCornerShape(8.dp),
                            color = if (isAllSelected) Color(0xFF2563EB) else Color.White,
                            border = BorderStroke(1.dp, if (isAllSelected) Color(0xFF2563EB) else Color(0xFFE2E8F0)),
                            modifier = Modifier.clickable { selectedCategoryId = "ALL" }
                        ) {
                            Text(
                                text = "Tümü (${suppliers.size})",
                                fontSize = 12.sp,
                                fontWeight = if (isAllSelected) FontWeight.Bold else FontWeight.Medium,
                                color = if (isAllSelected) Color.White else Color(0xFF334155),
                                modifier = Modifier.padding(horizontal = 12.dp, vertical = 7.dp)
                            )
                        }
                    }

                    // Dinamik Kategori Çipleri
                    items(categories) { category ->
                        val isSelected = selectedCategoryId == category.id
                        val count = suppliers.count { it.category_id == category.id }

                        Surface(
                            shape = RoundedCornerShape(8.dp),
                            color = if (isSelected) Color(0xFF2563EB) else Color.White,
                            border = BorderStroke(1.dp, if (isSelected) Color(0xFF2563EB) else Color(0xFFE2E8F0)),
                            modifier = Modifier.clickable { selectedCategoryId = category.id }
                        ) {
                            Row(
                                modifier = Modifier.padding(horizontal = 10.dp, vertical = 7.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(
                                    text = "${category.name} ($count)",
                                    fontSize = 12.sp,
                                    fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                                    color = if (isSelected) Color.White else Color(0xFF334155)
                                )

                                Spacer(modifier = Modifier.width(6.dp))

                                // Kategori Düzenle İkonu
                                Icon(
                                    imageVector = Icons.Default.Edit,
                                    contentDescription = "Düzenle",
                                    tint = if (isSelected) Color.White.copy(alpha = 0.8f) else Color(0xFF94A3B8),
                                    modifier = Modifier
                                        .size(14.dp)
                                        .clickable { categoryToEdit = category }
                                )
                            }
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(14.dp))

            // 3. Tedarikçiler Kompakt Grid (Sayfaya Çoklu Sığacak Şekilde)
            if (filteredSuppliers.isEmpty()) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(40.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Icon(Icons.Default.LocalShipping, contentDescription = null, tint = Color(0xFFCBD5E1), modifier = Modifier.size(48.dp))
                        Spacer(modifier = Modifier.height(8.dp))
                        Text("Kayıtlı veya filtreye uygun tedarikçi bulunamadı.", color = Color(0xFF94A3B8), fontSize = 14.sp)
                    }
                }
            } else {
                val gridState = androidx.compose.foundation.lazy.grid.rememberLazyGridState()
                LazyVerticalGrid(
                    state = gridState,
                    columns = GridCells.Adaptive(minSize = 200.dp),
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                    contentPadding = PaddingValues(bottom = 24.dp),
                    modifier = Modifier.fillMaxSize().verticalScrollbar(gridState)
                ) {
                    items(filteredSuppliers, key = { it.id ?: it.name }) { supplier ->
                        val categoryName = categories.find { it.id == supplier.category_id }?.name ?: "Genel"

                        SupplierCompactCard(
                            supplier = supplier,
                            categoryName = categoryName,
                            onEdit = { supplierToEdit = supplier },
                            onDelete = { supplier.id?.let { viewModel.deleteSupplier(it) } }
                        )
                    }
                }
            }
        }
    }

    // Kategori Ekle Modalı
    if (showAddCategoryDialog) {
        CategoryFormDialog(
            title = "Yeni Kategori Ekle",
            initialName = "",
            onDismiss = { showAddCategoryDialog = false },
            onSave = { name ->
                viewModel.addCategory(name)
                showAddCategoryDialog = false
            }
        )
    }

    // Kategori Düzenle / Sil Modalı
    if (categoryToEdit != null) {
        CategoryFormDialog(
            title = "Kategori Düzenle",
            initialName = categoryToEdit?.name ?: "",
            onDismiss = { categoryToEdit = null },
            onSave = { name ->
                categoryToEdit?.id?.let { id -> viewModel.updateCategory(id, name) }
                categoryToEdit = null
            },
            onDelete = {
                categoryToEdit?.id?.let { id -> viewModel.deleteCategory(id) }
                categoryToEdit = null
            }
        )
    }

    // Tedarikçi Ekle Modalı
    if (showAddSupplierDialog) {
        SupplierFormDialog(
            title = "Yeni Tedarikçi Ekle",
            initialSupplier = null,
            categories = categories,
            onDismiss = { showAddSupplierDialog = false },
            onSave = { catId, name, contact ->
                viewModel.addSupplier(catId, name, contact)
                showAddSupplierDialog = false
            }
        )
    }

    // Tedarikçi Düzenle / Sil Modalı
    if (supplierToEdit != null) {
        SupplierFormDialog(
            title = "Tedarikçi Düzenle",
            initialSupplier = supplierToEdit,
            categories = categories,
            onDismiss = { supplierToEdit = null },
            onSave = { catId, name, contact ->
                supplierToEdit?.id?.let { id -> viewModel.updateSupplier(id, catId, name, contact) }
                supplierToEdit = null
            },
            onDelete = {
                supplierToEdit?.id?.let { id -> viewModel.deleteSupplier(id) }
                supplierToEdit = null
            }
        )
    }
}

/**
 * Kompakt Tedarikçi Kartı
 */
@Composable
private fun SupplierCompactCard(
    supplier: Supplier,
    categoryName: String,
    onEdit: () -> Unit,
    onDelete: () -> Unit
) {
    Card(
        shape = RoundedCornerShape(10.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onEdit() }
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(12.dp),
            verticalArrangement = Arrangement.SpaceBetween
        ) {
            // Üst Satır: Tedarikçi Adı + Kategori Rozeti
            Column {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = supplier.name,
                        fontWeight = FontWeight.Bold,
                        fontSize = 15.sp,
                        color = Color(0xFF0F172A),
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f)
                    )

                    Surface(
                        shape = RoundedCornerShape(4.dp),
                        color = Color(0xFFF1F5F9),
                        border = BorderStroke(1.dp, Color(0xFFE2E8F0))
                    ) {
                        Text(
                            text = categoryName,
                            fontSize = 10.sp,
                            fontWeight = FontWeight.Medium,
                            color = Color(0xFF475569),
                            modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                        )
                    }
                }

                Spacer(modifier = Modifier.height(4.dp))

                // İletişim Bilgisi
                val contactText = if (supplier.contact_info.isNullOrBlank()) "İletişim bilgisi girilmedi" else (supplier.contact_info ?: "")
                Text(
                    text = contactText,
                    fontSize = 12.sp,
                    color = if (supplier.contact_info.isNullOrBlank()) Color(0xFF94A3B8) else Color(0xFF64748B),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }

            Spacer(modifier = Modifier.height(10.dp))

            // Alt Satır: Düzenle ve Sil Butonları
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Düzenle Butonu
                Surface(
                    shape = RoundedCornerShape(6.dp),
                    color = Color(0xFFEFF6FF),
                    modifier = Modifier.clickable { onEdit() }
                ) {
                    Row(
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(Icons.Default.Edit, contentDescription = "Düzenle", tint = Color(0xFF2563EB), modifier = Modifier.size(14.dp))
                        Spacer(modifier = Modifier.width(4.dp))
                        Text("Düzenle", fontSize = 11.sp, fontWeight = FontWeight.SemiBold, color = Color(0xFF2563EB))
                    }
                }

                Spacer(modifier = Modifier.width(6.dp))

                // Sil Butonu
                IconButton(
                    onClick = onDelete,
                    modifier = Modifier.size(28.dp)
                ) {
                    Icon(Icons.Default.Delete, contentDescription = "Sil", tint = Color(0xFFDC2626), modifier = Modifier.size(16.dp))
                }
            }
        }
    }
}

/**
 * Kategori Form Modalı (Ekle / Düzenle)
 */
@Composable
private fun CategoryFormDialog(
    title: String,
    initialName: String,
    onDismiss: () -> Unit,
    onSave: (String) -> Unit,
    onDelete: (() -> Unit)? = null
) {
    var name by remember { mutableStateOf(initialName) }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(16.dp),
        title = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(text = title, fontWeight = FontWeight.Bold, fontSize = 17.sp, color = Color(0xFF0F172A))
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Default.Close, contentDescription = "Kapat", tint = Color(0xFF64748B))
                }
            }
        },
        text = {
            Column(modifier = Modifier.fillMaxWidth()) {
                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = { Text("Kategori Adı") },
                    placeholder = { Text("Örn: İçecek, Mutfak, Temizlik...") },
                    singleLine = true,
                    shape = RoundedCornerShape(8.dp),
                    modifier = Modifier.fillMaxWidth(),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedContainerColor = Color.White,
                        unfocusedContainerColor = Color.White,
                        focusedBorderColor = Color(0xFF2563EB),
                        unfocusedBorderColor = Color(0xFFCBD5E1)
                    )
                )
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    if (name.isNotBlank()) {
                        onSave(name.trim())
                    }
                },
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                shape = RoundedCornerShape(8.dp)
            ) {
                Text("Kaydet", color = Color.White, fontWeight = FontWeight.SemiBold)
            }
        },
        dismissButton = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                if (onDelete != null) {
                    TextButton(
                        onClick = onDelete,
                        colors = ButtonDefaults.textButtonColors(contentColor = Color(0xFFDC2626))
                    ) {
                        Text("Sil", fontWeight = FontWeight.Medium)
                    }
                    Spacer(modifier = Modifier.width(4.dp))
                }
                TextButton(
                    onClick = onDismiss,
                    colors = ButtonDefaults.textButtonColors(contentColor = Color(0xFF64748B))
                ) {
                    Text("İptal")
                }
            }
        }
    )
}

/**
 * Tedarikçi Form Modalı (Ekle / Düzenle)
 */
@Composable
private fun SupplierFormDialog(
    title: String,
    initialSupplier: Supplier?,
    categories: List<SupplierCategory>,
    onDismiss: () -> Unit,
    onSave: (String, String, String) -> Unit,
    onDelete: (() -> Unit)? = null
) {
    var name by remember { mutableStateOf(initialSupplier?.name ?: "") }
    var contact by remember { mutableStateOf(initialSupplier?.contact_info ?: "") }
    var selectedCatId by remember { mutableStateOf(initialSupplier?.category_id ?: categories.firstOrNull()?.id ?: "") }
    var expandedCategoryDropdown by remember { mutableStateOf(false) }

    val currentCatName = categories.find { it.id == selectedCatId }?.name ?: "Kategori Seçin"

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(16.dp),
        title = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(text = title, fontWeight = FontWeight.Bold, fontSize = 17.sp, color = Color(0xFF0F172A))
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Default.Close, contentDescription = "Kapat", tint = Color(0xFF64748B))
                }
            }
        },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                // Kategori Dropdown
                Box(modifier = Modifier.fillMaxWidth()) {
                    OutlinedTextField(
                        value = currentCatName,
                        onValueChange = {},
                        readOnly = true,
                        label = { Text("Kategori") },
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable { expandedCategoryDropdown = !expandedCategoryDropdown },
                        trailingIcon = {
                            IconButton(onClick = { expandedCategoryDropdown = !expandedCategoryDropdown }) {
                                Icon(Icons.Default.ArrowDropDown, contentDescription = "Seç", tint = Color(0xFF64748B))
                            }
                        },
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedContainerColor = Color.White,
                            unfocusedContainerColor = Color.White,
                            focusedBorderColor = Color(0xFF2563EB),
                            unfocusedBorderColor = Color(0xFFCBD5E1)
                        )
                    )
                    DropdownMenu(
                        expanded = expandedCategoryDropdown,
                        onDismissRequest = { expandedCategoryDropdown = false },
                        modifier = Modifier.background(Color.White)
                    ) {
                        categories.forEach { cat ->
                            DropdownMenuItem(
                                text = { Text(cat.name, color = Color(0xFF1E293B)) },
                                onClick = {
                                    cat.id?.let { selectedCatId = it }
                                    expandedCategoryDropdown = false
                                }
                            )
                        }
                    }
                }

                // Tedarikçi Adı
                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = { Text("Tedarikçi Adı") },
                    placeholder = { Text("Örn: Metro Market, PürPak Kimya...") },
                    singleLine = true,
                    shape = RoundedCornerShape(8.dp),
                    modifier = Modifier.fillMaxWidth(),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedContainerColor = Color.White,
                        unfocusedContainerColor = Color.White,
                        focusedBorderColor = Color(0xFF2563EB),
                        unfocusedBorderColor = Color(0xFFCBD5E1)
                    )
                )

                // İletişim Bilgisi
                OutlinedTextField(
                    value = contact,
                    onValueChange = { contact = it },
                    label = { Text("İletişim Bilgisi / Tel (Opsiyonel)") },
                    placeholder = { Text("Örn: 0532 XXX XX XX / siparis@firma.com") },
                    singleLine = true,
                    shape = RoundedCornerShape(8.dp),
                    modifier = Modifier.fillMaxWidth(),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedContainerColor = Color.White,
                        unfocusedContainerColor = Color.White,
                        focusedBorderColor = Color(0xFF2563EB),
                        unfocusedBorderColor = Color(0xFFCBD5E1)
                    )
                )
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    if (name.isNotBlank()) {
                        onSave(selectedCatId, name.trim(), contact.trim())
                    }
                },
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                shape = RoundedCornerShape(8.dp)
            ) {
                Text("Kaydet", color = Color.White, fontWeight = FontWeight.SemiBold)
            }
        },
        dismissButton = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                if (onDelete != null) {
                    TextButton(
                        onClick = onDelete,
                        colors = ButtonDefaults.textButtonColors(contentColor = Color(0xFFDC2626))
                    ) {
                        Text("Sil", fontWeight = FontWeight.Medium)
                    }
                    Spacer(modifier = Modifier.width(4.dp))
                }
                TextButton(
                    onClick = onDismiss,
                    colors = ButtonDefaults.textButtonColors(contentColor = Color(0xFF64748B))
                ) {
                    Text("İptal")
                }
            }
        }
    )
}
